import type { FC } from "react";

interface VacuumControlsProps {
	power: number;
	range: number;
	showImage: boolean;
	onPowerChange: (power: number) => void;
	onRangeChange: (range: number) => void;
	onShowImageChange: (show: boolean) => void;
	onClearPoints: () => void;
	// Canvas refs for immediate toggle
	visibleCanvasRef: React.RefObject<HTMLCanvasElement | null>;
	imageLoaded: boolean;
	referenceImage: HTMLImageElement | null;
	imageWidth: number;
	imageHeight: number;
	showGrid: boolean;
}

export const VacuumControls: FC<VacuumControlsProps> = ({
	power,
	range,
	showImage,
	onPowerChange,
	onRangeChange,
	onShowImageChange,
	onClearPoints,
	visibleCanvasRef,
	imageLoaded,
	referenceImage,
	imageWidth,
	imageHeight,
	showGrid,
}) => {
	const handleShowImageToggle = (checked: boolean) => {
		onShowImageChange(checked);

		// Immediately update canvas without full effect redraw
		const canvas = visibleCanvasRef.current;
		if (canvas && imageLoaded && referenceImage) {
			const ctx = canvas.getContext("2d", { willReadFrequently: true });
			if (ctx) {
				if (checked) {
					// Show image - just draw the reference image
					ctx.drawImage(referenceImage, 0, 0, imageWidth, imageHeight);

					// If grid is enabled, draw it immediately
					if (showGrid) {
						const gridSize = 20;
						const cols = gridSize;
						const rows = gridSize;
						ctx.save();
						ctx.strokeStyle = "rgba(144, 238, 144, 0.7)"; // light green
						ctx.lineWidth = 1;
						for (let i = 0; i <= rows; i++) {
							const y = (i / rows) * imageHeight;
							ctx.beginPath();
							ctx.moveTo(0, y);
							ctx.lineTo(imageWidth, y);
							ctx.stroke();
						}
						for (let j = 0; j <= cols; j++) {
							const x = (j / cols) * imageWidth;
							ctx.beginPath();
							ctx.moveTo(x, 0);
							ctx.lineTo(x, imageHeight);
							ctx.stroke();
						}
						ctx.restore();
					}
				} else {
					// Hide image - fill with white
					ctx.fillStyle = "#fff";
					ctx.fillRect(0, 0, imageWidth, imageHeight);
				}
			}
		}
	};

	return (
		<div className="space-y-4">
			<div className="text-sm text-gray-600 mb-4">
				<p>
					Click and drag anywhere on the image to create vacuum effects. Each
					drag creates a new vacuum point that creates a black hole compression
					effect that shrinks the image around the point.
				</p>
			</div>

			{/* View Controls */}
			<div className="space-y-2">
				<div className="flex items-center gap-2">
					<input
						type="checkbox"
						id="vacuum-show-image"
						checked={showImage}
						onChange={(e) => handleShowImageToggle(e.target.checked)}
						className="mr-2"
					/>
					<label htmlFor="vacuum-show-image" className="text-sm font-medium">
						Show Image
					</label>
				</div>
			</div>

			{/* Vacuum Controls */}
			<div className="space-y-3">
				<div>
					<label
						htmlFor="vacuum-power"
						className="block text-sm font-medium mb-1"
					>
						Power: {power.toFixed(1)}
					</label>
					<input
						id="vacuum-power"
						type="range"
						min="0.1"
						max="3.0"
						step="0.1"
						value={power}
						onChange={(e) => onPowerChange(Number.parseFloat(e.target.value))}
						className="w-full"
					/>
				</div>

				<div>
					<label
						htmlFor="vacuum-range"
						className="block text-sm font-medium mb-1"
					>
						Range: {range}px
					</label>
					<input
						id="vacuum-range"
						type="range"
						min="50"
						max="300"
						step="10"
						value={range}
						onChange={(e) => onRangeChange(Number.parseInt(e.target.value))}
						className="w-full"
					/>
				</div>
			</div>

			<button
				type="button"
				onClick={onClearPoints}
				className="w-full px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
			>
				Clear All Points
			</button>
		</div>
	);
};
